import {
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import { useDAL } from '@/contexts/useDAL';

import { EmptySelect } from './emptySelect';

export const GcpSubnetSelect = ({
  accountId,
  region,
  subnetId,
  onChange,
  allowOnlyPeeredNetworks,
}: {
  accountId?: string;
  region?: string;
  subnetId?: number;
  onChange: (
    subnetName: string,
    subnetId: number,
    networkName?: string,
    networkHostProject?: string
  ) => void;
  allowOnlyPeeredNetworks?: boolean;
}) => {
  if (!accountId || !region) {
    return <EmptySelect title='Network' selectClass='h-[58px]' />;
  }

  return (
    <InnerGcpSubnetSelect
      accountId={accountId}
      region={region}
      subnetId={subnetId}
      onChange={onChange}
      allowOnlyPeeredNetworks={allowOnlyPeeredNetworks}
    />
  );
};

const InnerGcpSubnetSelect = ({
  accountId,
  region,
  subnetId,
  onChange,
  allowOnlyPeeredNetworks,
}: {
  accountId: string;
  region: string;
  subnetId?: number;
  onChange: (
    subnetName: string,
    subnetId: number,
    networkName?: string,
    networkHostProject?: string
  ) => void;
  allowOnlyPeeredNetworks?: boolean;
}) => {
  const dal = useDAL();
  const { data: ownSubnets } = dal.restore.gcp.listSubnets(accountId, region);
  const { data: sharedSubnets } = dal.restore.gcp.listSharedSubnets(
    accountId,
    region
  );

  const body = {
    subnets: [
      ...(ownSubnets?.subnets || []),
      ...(sharedSubnets?.subnets || []),
    ],
  };

  if (!body.subnets.length) {
    return <EmptySelect title='Network' selectClass='h-[58px]' />;
  }

  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>Network</FormLabel>
      <Select
        className='h-[58px]'
        size='small'
        value={subnetId || ''}
        onChange={(event) => {
          const selectedSubnet = body.subnets.find(
            (s) => s.id === event.target.value
          );
          if (selectedSubnet)
            onChange(
              selectedSubnet.name,
              event.target.value as number,
              selectedSubnet.vpcNetwork,
              selectedSubnet.project
            );
        }}
      >
        {body.subnets.map((subnet) => {
          const isDisabled = allowOnlyPeeredNetworks && !subnet.isPeered;
          const isShared = sharedSubnets?.subnets?.some(
            (s) => s.id === subnet.id
          );

          return (
            <MenuItem key={subnet.id} value={subnet.id} disabled={isDisabled}>
              <Stack direction={'column'} className='py-[4px]'>
                <Typography
                  color={
                    isDisabled ? 'var(--mui-palette-text-gray1)' : undefined
                  }
                >
                  {subnet.name}
                  {isShared && (
                    <Typography
                      component='span'
                      variant='caption'
                      className='ml-2'
                      color='var(--mui-palette-text-secondary)'
                    >
                      (Shared from {subnet.project})
                    </Typography>
                  )}
                </Typography>
                <Typography variant='body2' className='mt-[4px]'>
                  {subnet.vpcNetwork}
                </Typography>
              </Stack>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};
